/* eslint-disable no-useless-escape */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
// import Tesseract from 'tesseract.js';
import { useLanguage } from '../App';
import { useTheme } from '../theme';
import { apiUrl, fileUrl } from '../api';

// Parse fields from OCR-ed text (deterministic, regex-based)
// eslint-disable-next-line no-unused-vars
function parseFieldsFromText(text) {
  const out = {}; const T = (text || '').replace(/\r/g, '').split(/\n+/).map(l => l.trim()).filter(Boolean); const fullRaw = T.join(' ');
  const full = fullRaw.replace(/[|]/g, 'I'); // common OCR artifact
  const cleanDigits = (s='') => s.toUpperCase().replace(/[OQ]/g,'0').replace(/[I]/g,'1').replace(/B/g,'8').replace(/S/g,'5').replace(/[^0-9]/g,'');
  const cleanLetters = (s='') => s.toUpperCase().replace(/[^A-ZĂÂÎȘȚ]/g,'');

  // CNP - prefer label, else first 13 digits sequence
  let cnp = (full.match(/CNP\s*[:-]?\s*([0-9OIQS\s]{13,})/i)?.[1]) || (full.match(/\b([0-9OIQS]{13})\b/)?.[1]);
  if (cnp) {
    const d = cleanDigits(cnp);
    if (d.length >= 13) out.cnp = d.slice(0,13);
  }

  // Serie/Număr CI - via labels
  const serieMatch = full.match(/(?:SERIA|SERIE|CI)\s*[:-]?\s*([A-ZĂÂÎȘȚ]{2})\b/i);
  if (serieMatch) out.ciSeries = cleanLetters(serieMatch[1]).slice(0,2);
  let nrMatch = full.match(/(?:NR.?|NUMAR|NO.?|NRUL)\s*[:-]?\s*([0-9OIQS]{4,8})/i);
  if (nrMatch) {
    const d = cleanDigits(nrMatch[1]);
    if (d.length >= 6) out.ciNumber = d.slice(0,6);
  }
  // Fallback: pattern like AA 123456 anywhere
  if ((!out.ciSeries || !out.ciNumber)) {
    const anySerieNr = full.match(/\b([A-Z]{2})\s*([0-9OIQS]{6})\b/);
    if (anySerieNr) {
      if (!out.ciSeries) out.ciSeries = cleanLetters(anySerieNr[1]).slice(0,2);
      if (!out.ciNumber) out.ciNumber = cleanDigits(anySerieNr[2]).slice(0,6);
    }
  }

  // Nume / Prenume
  const numeMatch = full.match(/(?:NUME|NUNE|SURNUME|SURNUME)?\s*[:-]?\s*([A-ZĂÂÎȘȚ-\s]{2,})\s+(?:PRENUME|GIVEN\s+NAME|PRENUME)/i) || full.match(/(?:NUME)\s*[:-]?\s*([A-ZĂÂÎȘȚ-\s]{2,})/i);
  const prenumeMatch = full.match(/(?:PRENUME|GIVEN\s+NAME)\s*[:-]?\s*([A-ZĂÂÎȘȚ-\s]{2,})/i);
  if (numeMatch) out.lastName = numeMatch[1].trim().replace(/\s+/g, ' ');
  if (prenumeMatch) out.firstName = prenumeMatch[1].trim().replace(/\s+/g, ' ');
  // Heuristic fallback for names if labels missing: take two longest uppercase tokens not being labels
  if (!out.lastName || !out.firstName) {
    const lines = (text||'').split(/\n+/).map(s=>s.trim()).filter(Boolean);
    const candidates = lines.filter(l => /^[A-ZĂÂÎȘȚ-\s]{5,}$/.test(l) && !/(CNP|SERIA|NUMAR|ADRESA|DOMICILIU|EMITENT|VALABIL)/i.test(l));
    if (candidates.length) {
      const sorted = candidates.sort((a,b)=>b.length-a.length);
      const parts = sorted[0].split(/\s+/);
      if (parts.length>=2) {
        if (!out.lastName) out.lastName = parts[0];
        if (!out.firstName) out.firstName = parts.slice(1).join(' ');
      }
    }
  }

  // Domiciliu / Adresă (prinde până la virgulă sau următorul label)
  const addrMatch = full.match(/(?:DOMICILIU|ADRESA|ADRESĂ)\s*[:\-]?\s*([^,\n]+(?:,\s*[^,\n]+){0,3})/i); if (addrMatch) out.address = addrMatch[1].trim();
  const issMatch = full.match(/(?:EMITENT|ELIBERAT DE)\s*[:\-]?\s*([^,\n]+)/i); if (issMatch) out.issuer = issMatch[1].trim();
  const expMatch = full.match(/(\d{2}[.\/-]\d{2}[.\/-]\d{4}|\d{4}-\d{2}-\d{2})/); if (expMatch) { const v = expMatch[1]; if (/\d{4}-\d{2}-\d{2}/.test(v)) out.expireAt = v; else { const [d,m,y] = v.replace(/[\/]/g, '.').split('.'); if (y&&m&&d) out.expireAt = `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`; } }
  return out;
}

// Validate Romanian CNP using checksum 279146358279
function isValidCNP(cnp) {
  const v = (cnp || '').replace(/\D/g, '');
  if (v.length !== 13) return false;
  const ctrl = '279146358279'.split('').map(Number);
  const sum = v
    .slice(0, 12)
    .split('')
    .map(Number)
    .reduce((acc, d, i) => acc + d * ctrl[i], 0);
  let c = sum % 11;
  if (c === 10) c = 1;
  return c === Number(v[12]);
}

// Helpers from Cititor Buletin 1 (adapted to React)
const stripDiacritics = (s) => (s ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : s);
const normalizeCedillaToASCII = (s) => {
  if (!s) return s;
  return String(s)
    .replace(/\u015E/g,'S')
    .replace(/\u015F/g,'s')
    .replace(/\u0162/g,'T')
    .replace(/\u0163/g,'t');
};
const matchJudetName = (input) => {
  if (!input) return '';
  const options = [
    'Alba','Arad','Argeș','Bacău','Bihor','Bistrița-Năsăud','Botoșani','Brașov','Brăila','București','Buzău','Caraș-Severin','Călărași','Cluj','Constanța','Covasna','Dâmbovița','Dolj','Galați','Giurgiu','Gorj','Harghita','Hunedoara','Ialomița','Iași','Ilfov','Maramureș','Mehedinți','Mureș','Neamț','Olt','Prahova','Satu Mare','Sălaj','Sibiu','Suceava','Tulcea','Timiș','Teleorman','Vâlcea','Vaslui','Vrancea'
  ];
  const normInput = stripDiacritics(String(input)).toLowerCase();
  const countyMap = { AB:'Alba', AR:'Arad', AG:'Argeș', BC:'Bacău', BH:'Bihor', BN:'Bistrița-Năsăud', BT:'Botoșani', BR:'Brăila', BV:'Brașov', BZ:'Buzău', CL:'Călărași', CS:'Caraș-Severin', CJ:'Cluj', CT:'Constanța', CV:'Covasna', DB:'Dâmbovița', DJ:'Dolj', GL:'Galați', GR:'Giurgiu', GJ:'Gorj', HR:'Harghita', HD:'Hunedoara', IL:'Ialomița', IS:'Iași', IF:'Ilfov', MM:'Maramureș', MH:'Mehedinți', MS:'Mureș', NT:'Neamț', OT:'Olt', PH:'Prahova', SM:'Satu Mare', SJ:'Sălaj', SB:'Sibiu', SV:'Suceava', TL:'Tulcea', TM:'Timiș', TR:'Teleorman', VL:'Vâlcea', VS:'Vaslui', VN:'Vrancea', B:'București' };
  const codeMatch = String(input).toUpperCase().match(/\b([A-Z]{1,2})\b/);
  if (codeMatch && countyMap[codeMatch[1]]) return countyMap[codeMatch[1]];
  for (const opt of options) if (stripDiacritics(opt).toLowerCase() === normInput) return opt;
  for (const opt of options) if (normInput.includes(stripDiacritics(opt).toLowerCase())) return opt;
  return '';
};
const extractAddress = (joined) => {
  const out = {};
  let segment = (joined.match(/DOMICILIU\s*[:-]?\s*([\s\S]{0,220})/i)?.[1]) || joined;
  segment = segment.replace(/\.(?=\S)/g, '. ');
  const mJ = segment.match(/\b(JUDETUL|JUDET|JUD.)\s*[:-]?\s*([A-ZĂÂÎȘȚa-zăâîșț-\s]{2,})\b/i);
  if (mJ) out.judet = mJ[2].trim();
  const mJCode = segment.match(/\bJUD.?\s*([A-Z]{1,2})\b/i) || segment.match(/\bJUD.[\s]*([A-Z]{1,2})\b/i) || segment.match(/\bJUD\s*[:-]?\s*([A-Z]{1,2})\b/i);
  if (!out.judet && mJCode) out.judet = mJCode[1];
  const mLoc = segment.match(/\b(MUNICIPIUL|MUN.?|ORAȘUL|ORASUL|ORAȘ|ORAS|COMUNA|COM.)\s*[:-]?\s*([A-ZĂÂÎȘȚa-zăâîșț\s-]{2,})/i);
  if (mLoc) out.localitate = mLoc[2].trim();
  const mSector = segment.match(/\bSECTOR\s*[:-]?\s*(\d{1,2})\b/i);
  if (mSector) out.satSector = `Sector ${mSector[1]}`;
  const mSat = segment.match(/\bSAT.?\s*[:-]?\s*([A-ZĂÂÎȘȚa-zăâîșț\s-]{2,})/i);
  if (mSat) out.satSector = mSat[1].trim();
  const mStr = segment.match(/\b(STR.?|STRADA)\s*[:-]?\s*([\wĂÂÎȘȚăâîșț.\s-]+?)(?=,|\bNR\b|\bNUM\b|\bBL\b|\bSC\b|\bET\b|\bAP\b|\n|$)/i);
  if (mStr) out.strada = mStr[2].trim().replace(/.$/, '');
  const mNr = segment.match(/\b(NR.?|NUMAR|NUMĂR)\s*[:-]?\s*([\w-]+)\b/i);
  if (mNr) out.numar = mNr[2].trim();
  const mBl = segment.match(/\b(BL.?|BLOC)\s*[:-]?\s*([\w-]+)\b/i);
  if (mBl) out.bloc = mBl[2].trim();
  const mSc = segment.match(/\b(SC.?|SCARA)\s*[:-]?\s*([\w-]+)\b/i);
  if (mSc) out.scara = mSc[2].trim();
  const mEt = segment.match(/\b(ET.?|ETAJ)\s*[:-]?\s*([\w-]+)\b/i);
  if (mEt) out.etaj = mEt[2].trim();
  const mAp = segment.match(/\b(AP.?|APARTAMENT)\s*[:-]?\s*([\w-]+)\b/i);
  if (mAp) out.apartament = mAp[2].trim();
  const mCp = segment.match(/\b(COD\s*PO(?:STAL)?|CP|C.P.)\s*[:-]?\s*(\d{6})\b/i);
  if (mCp) out.codPostal = mCp[2];
  if (out.judet) {
    const matched = matchJudetName(out.judet);
    if (matched) out.judet = matched;
  }
  return out;
};
const extractNameFromLines = (lines) => {
  const isUpper = (s) => /^[A-ZȘȚĂÂÎ][A-ZȘȚĂÂÎ-\s]{1,}$/.test(s.trim());
  const findNextUpperAfter = (idx) => {
    for (let i=idx+1;i<Math.min(lines.length, idx+5);i++){
      const L = lines[i].trim();
      if (isUpper(L)) return L.replace(/\s{2,}/g,' ');
    }
    return '';
  };
  const idxNume = lines.findIndex(l => /\bNUME\b|\bSURNAME\b/i.test(l));
  const idxPren = lines.findIndex(l => /\bPRENUME\b|\bPRENOM\b|\bFIRST\s*NAME\b/i.test(l));
  const surname = idxNume !== -1 ? findNextUpperAfter(idxNume) : '';
  const given   = idxPren !== -1 ? findNextUpperAfter(idxPren) : '';
  const full = [surname, given].filter(Boolean).join(' ').trim();
  return { surname, given, full };
};
const extractGivenBetweenLabels = (joined) => {
  const start = joined.search(/\b(PRENUME|PRENOM|FIRST\s*NAME)\b/i);
  if (start === -1) return '';
  const after = joined.slice(start);
  const m = after.search(/\b(CET(?:\s*Ă|A)ȚENIE|NATIONALITATE|NATIONALITY|LOC\s+NA(?:Ș|S)TERII|LIEU\s+DE\s+NAISSANCE|PLACE\s+OF\s+BIRTH|DOMICILIU|ADRESSE|ADDRESS|SEX|SEXE)\b/i);
  const endIdx = m === -1 ? after.length : m;
  const segment = after.slice(0, endIdx);
  const matches = segment.match(/([A-ZȘȚĂÂÎ]{2,}(?:[-\s][A-ZȘȚĂÂÎ]{2,}){0,3})/g);
  if (!matches) return '';
  return matches.sort((a,b)=>b.length-a.length)[0].trim().replace(/\s{2,}/g,' ');
};
const deriveSexFromCNP = (cnp) => {
  if (!/^\d{13}$/.test(cnp)) return '';
  const s = parseInt(cnp[0], 10);
  if (isNaN(s)) return '';
  return (s % 2 === 1) ? 'Masculin' : 'Feminin';
};
const deriveBirthDateFromCNP = (cnp) => {
  if (!/^\d{13}$/.test(cnp)) return '';
  const s = parseInt(cnp[0],10);
  const yy = parseInt(cnp.slice(1,3),10);
  const mm = cnp.slice(3,5);
  const dd = cnp.slice(5,7);
  let century = 1900;
  if (s === 1 || s === 2 || s === 7 || s === 8) century = 1900;
  else if (s === 3 || s === 4) century = 1800;
  else if (s === 5 || s === 6) century = 2000;
  else if (s === 9) century = 1900;
  const yyyy = century + yy;
  return `${dd}.${mm}.${yyyy}`;
};
const normalizeDate = (s) => {
  if (!s) return '';
  const m = String(s).match(/(\d{2})[.\/-](\d{2})[.\/-](\d{2,4})/);
  if (!m) return String(s).trim();
  let [_, dd, mm, yy] = m;
  if (yy.length === 2){ const n = parseInt(yy,10); yy = n >= 50 ? `19${yy}` : `20${yy}`; }
  return `${dd}.${mm}.${yy}`;
};
const extractFieldsFromText = (text) => {
  const cleanDigits = (s='') => String(s)
    .replace(/[|]/g,'1')
    .replace(/[OQ]/g,'0')
    .replace(/[Il]/g,'1')
    .replace(/B/g,'8')
    .replace(/S/g,'5')
    .replace(/Z/g,'2')
    .replace(/[^0-9]/g,'');
  const raw = normalizeCedillaToASCII(text)
    .replace(/[|]/g,'I')
    .replace(/\u00A0/g,' ')
    .replace(/[\t]+/g,' ')
    .replace(/[ ]{2,}/g,' ');
  const lines = raw.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
  const joined = lines.join('\n');
  const result = {};
  // CNP with label or any plausible 11-13 length token (letters may be OCR-misread digits)
  let cnpMatch = joined.match(/\bCNP\s*[:\-]?\s*([0-9OIQSlBZS\s]{11,18})\b/i) || text.match(/\bCNP\s*[:\-]?\s*([0-9OIQSlBZS\s]{11,18})\b/i);
  if (!cnpMatch) {
    const compact = text.replace(/\s+/g,'');
    const candidates = compact.match(/[0-9OIQSlBZS]{11,13}/g) || [];
    const first13 = candidates.find(c => cleanDigits(c).length === 13);
    if (first13) cnpMatch = [first13, first13];
  }
  if (cnpMatch) {
    const d = cleanDigits(cnpMatch[1]);
    if (d.length >= 13) result.cnp = d.slice(0,13);
  }
  const serieLabel = joined.match(/\bSER(?:IA|IE)\s*[:\-]?\s*([A-Z]{2})\b/i);
  if (serieLabel) result.serieCI = serieLabel[1].toUpperCase();
  // Allow space between series letters (e.g., "B C 123456")
  const nrLabel = joined.match(/\bNR\.?\s*[:\-]?\s*([0-9OIQSlBZS]{6,8})\b/i);
  if (nrLabel) {
    const d = cleanDigits(nrLabel[1]);
    if (d.length >= 6) result.numarCI = d.slice(0,6);
  }
  if (!result.serieCI || !result.numarCI){
    const combo = joined.match(/\b([A-Z])\s*([A-Z])\s*([0-9OIQSlBZS]{6})\b/) || joined.match(/\b([A-Z]{2})\s*([0-9OIQSlBZS]{6})\b/) || text.match(/\b([A-Z]{2})\s*([0-9OIQSlBZS]{6})\b/);
    if (combo){
      if (!result.serieCI) {
        const ser = (combo[2] && combo.length===4) ? (combo[1] + combo[2]) : combo[1];
        result.serieCI = String(ser).replace(/\s+/g,'').slice(0,2).toUpperCase();
      }
      if (!result.numarCI) result.numarCI = cleanDigits(combo[combo.length-1]).slice(0,6);
    }
  }
  const cleanNamePart = (name) => {
    if (!name) return '';
    let cleaned = normalizeCedillaToASCII(name).replace(/[^A-ZȘȚĂÂÎ\-\s]/g, ' ').trim().replace(/\s+/g, ' ');
    cleaned = cleaned.split(' ').filter(word => word.length >= 2).join(' ');
    return cleaned;
  };
  const BANNED_TOKENS = new Set(['HESTER','PRENUME','PRENOM','FIRST','NAME','NUME','SURNAME','NOM','LAST']);
  const LABELISH_ROOTS = ['PRENUM','PRENOM','FIRST','NUME','SURNAME','NOM','LAST'];
  const SHORT_NAME_ALLOW = new Set(['ION','DAN','ANA']);
  const lineHasLabelish = (s='') => /PRENUM|PRENOM|FIRST|NUME|SURNAME|NOM|LAST/i.test(stripDiacritics(s));
  const isBannedToken = (w) => { if (!w) return false; const t = stripDiacritics(String(w)).toUpperCase(); return BANNED_TOKENS.has(t) || LABELISH_ROOTS.some(r => t.startsWith(r)); };
  const isLikelyNameToken = (w, surnameRef='') => {
    if (!w) return false; const t = stripDiacritics(String(w)).toUpperCase();
    if (isBannedToken(t)) return false; if (t === stripDiacritics(String(surnameRef)).toUpperCase()) return false; if (/\d/.test(t)) return false; if (t.length >= 4) return true; return SHORT_NAME_ALLOW.has(t);
  };
  const firstValidTokenFromText = (text, surnameRef='') => { const tokens = cleanNamePart(text).split(/\s+/).filter(Boolean); for (const t of tokens){ if (isLikelyNameToken(t, surnameRef)) return t; } return ''; };
  const findNextValidNameToken = (linesArr, startIndex, surnameRef='') => { for (let i = Math.max(0, startIndex); i < Math.min(linesArr.length, startIndex + 8); i++){ const line = String(linesArr[i] || ''); if (lineHasLabelish(line)) continue; const token = firstValidTokenFromText(line, surnameRef); if (token) return { token, index: i }; } return { token: '', index: -1 }; };
  const surnameIdx = lines.findIndex(l => /Nume.*Nom.*Last/i.test(l) || /^Nume$/i.test(l) || /Surname/i.test(l));
  let surname = '', firstName = '';
  if (surnameIdx !== -1) {
    for (let i = surnameIdx + 1; i < Math.min(surnameIdx + 6, lines.length); i++) {
      const line = lines[i].trim();
      const cleaned = cleanNamePart(line);
      if (cleaned && cleaned.length >= 2 && !/Nume|Nom|Last|name|Prenume|Prenom|First|Cetățenie|Naționalitate/i.test(cleaned)) {
        const firstWord = cleaned.split(/\s+/)[0];
        if (firstWord && firstWord.length >= 2) { surname = firstWord; break; }
      }
    }
  }
  let firstNameIdx = lines.findIndex(l => /Prenume.*Prenom.*First/i.test(l));
  if (firstNameIdx === -1) firstNameIdx = lines.findIndex(l => /Prenume/i.test(l) && /Prenom/i.test(l));
  if (firstNameIdx === -1) firstNameIdx = lines.findIndex(l => /First.*name/i.test(l));
  if (firstNameIdx === -1) firstNameIdx = lines.findIndex(l => /^Prenume$/i.test(l.trim()));
  if (firstNameIdx !== -1) {
    const { token } = findNextValidNameToken(lines, firstNameIdx + 1, surname);
    if (token) firstName = token;
  }
  if (!firstName) {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const diacriticMatch = line.match(/\bȘ[A-ZȘȚĂÂÎ]+/);
      const asciiCommon = line.match(/\b(STEFAN|SERBAN)\b/);
      const found = (diacriticMatch && diacriticMatch[0]) || (asciiCommon && asciiCommon[1]);
      if (found) {
        const potentialName = cleanNamePart(found);
        if (potentialName && potentialName.length >= 4 && potentialName !== surname && !isBannedToken(potentialName)) { firstName = potentialName; break; }
      }
    }
  }
  if (!firstName && surname) {
    const surnameLineIdx = lines.findIndex(l => l.includes(surname));
    if (surnameLineIdx !== -1) {
      const { token } = findNextValidNameToken(lines, surnameLineIdx + 1, surname);
      if (token) firstName = token;
    }
  }
  if (!surname || !firstName) {
    const nameParts = extractNameFromLines(lines);
    if (!surname && nameParts.surname) surname = nameParts.surname;
    if (!firstName && nameParts.given) firstName = nameParts.given;
  }
  if (!firstName) {
    const given = extractGivenBetweenLabels(joined);
    if (given) firstName = given;
  }
  if (!firstName || firstName === surname){
    const startIdx = (() => { const si = lines.findIndex(l => l.includes(surname)); if (si !== -1) return si + 1; if (firstNameIdx !== -1) return firstNameIdx + 1; return 0; })();
    const { token } = findNextValidNameToken(lines, startIdx, surname);
    if (token && token !== surname) firstName = token;
  }
  if (surname && !firstName) {
    const parts = surname.trim().split(/\s+/);
    if (parts.length >= 2) { result.nume = parts[0]; result.prenume = parts.slice(1).join(' '); }
    else { result.nume = surname; result.prenume = ''; }
  } else {
    result.nume = surname; result.prenume = firstName;
  }
  Object.assign(result, extractAddress(joined));
  const sexLabel = joined.match(/\bSEX\s*[:\-]?\s*(MASCULIN|FEMININ|M|F)\b/i);
  if (sexLabel){ const v = sexLabel[1].toUpperCase(); result.sex = (v === 'M' || v === 'MASCULIN') ? 'Masculin' : 'Feminin'; }
  const cetLabel = joined.match(/\bCET(?:Ă|A)ȚENIE\s*[:\-]?\s*([^\n]{2,})/i); if (cetLabel) result.cetatenie = cetLabel[1].trim().replace(/\s{2,}/g,' ');
  const dnLabel = joined.match(/\bDATA\s+NA(?:Ș|S)TERII\s*[:\-]?\s*([\d.\/-]{8,10})/i); if (dnLabel) result.dataNasterii = normalizeDate(dnLabel[1]);
  const lnLabel = joined.match(/\bLOC\s+NA(?:Ș|S)TERII\s*[:\-]?\s*([^\n]{2,})/i); if (lnLabel) result.locNasterii = lnLabel[1].trim();
  const elibLabel = joined.match(/\b(ELIBERAT\s+DE|EMIS\s+DE|AUTORITATEA\s+ELIBERATOARE)\s*[:\-]?\s*([^\n]{2,})/i); if (elibLabel) result.eliberatDe = elibLabel[2].trim();
  const deLabel = joined.match(/\bDATA\s+ELIBER(?:Ă|A)RII\s*[:\-]?\s*([\d.\/-]{8,10})/i); if (deLabel) result.dataEliberarii = normalizeDate(deLabel[1]);
  const vpLabel = joined.match(/\bVALABIL(?:\s*P(?:Â|A)N(?:\s?Ă|A)\s*LA)?\s*[:\-]?\s*([\d.\/-]{8,10})/i); if (vpLabel) result.valabilPanaLa = normalizeDate(vpLabel[1]);
  if (result.cnp){ if (!result.sex) result.sex = deriveSexFromCNP(result.cnp); if (!result.dataNasterii) result.dataNasterii = deriveBirthDateFromCNP(result.cnp); }
  return result;
};

export default function GenerateContract() {
  const { language } = useLanguage();
  const { tokens, themeKey } = useTheme();
  const t = {
    en: {
      title: 'Generate Contract',
      chooseCar: 'Which car do you want to generate the contract for?',
      pickCar: 'Select car',
      buyer: 'Buyer details',
      uploadId: 'Upload ID card photo',
      uploadIdBack: 'Upload back side (optional)',
      extractFromId: 'Extract from ID',
      orFillManually: 'or fill manually',
      firstName: 'First name',
      lastName: 'Last name',
      cnp: 'CNP',
      ciSeries: 'ID Series',
      ciNumber: 'ID Number',
    address: 'Address',
    street: 'Street',
    streetNo: 'Number',
  county: 'County',
  postalCode: 'Postal code',
  villageOrSector: 'Village/Sector',
      issuer: 'Issuer',
      expireAt: 'Expiry date',
      seller: 'Seller (pre-filled)',
      generate: 'Generate contract (PDF)',
      pickCarPlaceholder: 'Choose a car...',
      statusIdle: 'Ready',
      statusOcrRunning: 'Reading ID…',
      statusGenerating: 'Generating contract…',
  note: 'Note: OCR and PDF generation require backend endpoints. If they are not available yet, data will not be extracted and the action will show an error message.',
  mapTitle: 'PDF field mapping (once)',
  mapHelp: 'Pick which actual PDF field each data value should fill. This is needed because the fillable PDF uses generic field names.',
  mapOpen: 'Configure PDF mapping',
  mapClose: 'Hide mapping',
  mapAutoSaved: 'Mapping auto-saved',
  mapReset: 'Reset mapping',
  mapIndexedPreview: 'Open indexed preview',
  mapUnassigned: '— unassigned —',
    },
    ro: {
      title: 'Generează Contract',
      chooseCar: 'La ce mașină vrei să generezi contractul?',
      pickCar: 'Selectează mașina',
      buyer: 'Date cumpărător',
      uploadId: 'Încarcă poza buletinului (față)',
      uploadIdBack: 'Încarcă verso (opțional)',
      extractFromId: 'Extrage din buletin',
      orFillManually: 'sau completează manual',
      firstName: 'Prenume',
      lastName: 'Nume',
      cnp: 'CNP',
      ciSeries: 'Serie CI',
      ciNumber: 'Număr CI',
    address: 'Adresă',
    street: 'Stradă',
    streetNo: 'Nr',
  county: 'Județ',
  postalCode: 'Cod poștal',
  villageOrSector: 'Sat/Sector',
      issuer: 'Emitent',
      expireAt: 'Data expirării',
      seller: 'Vânzător (prefilled)',
      generate: 'Generează contract (PDF)',
      pickCarPlaceholder: 'Alege o mașină…',
      statusIdle: 'Pregătit',
      statusOcrRunning: 'Se citește buletinul…',
      statusGenerating: 'Se generează contractul…',
  note: 'Notă: OCR și generarea PDF necesită endpoint-uri pe backend. Dacă încă nu sunt disponibile, datele nu vor fi extrase și acțiunea va returna mesaj de eroare.',
  mapTitle: 'Mapare câmpuri PDF (o dată)',
  mapHelp: 'Alege pentru fiecare valoare din date câmpul real din PDF în care să fie completată. E necesar pentru că PDF-ul completabil are nume generice pentru câmpuri.',
  mapOpen: 'Configurează maparea PDF',
  mapClose: 'Ascunde maparea',
  mapAutoSaved: 'Maparea se salvează automat',
  mapReset: 'Resetează',
  mapIndexedPreview: 'Deschide previzualizarea indexată',
  mapUnassigned: '— nealocat —',
    },
  }[language || 'ro'];

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 760;
  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState('');
  const [status, setStatus] = useState('idle');
  const location = useLocation();

  // Preselect car when navigated from a details page (admin flow)
  useEffect(() => {
    const preId = location?.state?.preselectedCarId;
    if (preId && !selectedCarId) setSelectedCarId(preId);
  }, [location?.state?.preselectedCarId, selectedCarId]);

  // Also allow ?carId=... or ?id=... query parameters
  useEffect(() => {
    if (selectedCarId) return; // already chosen
    try {
      const sp = new URLSearchParams(window.location.search);
      const qp = sp.get('carId') || sp.get('id');
      if (qp) setSelectedCarId(qp);
    } catch {}
  }, [selectedCarId]);
  const [ocrProgress, setOcrProgress] = useState(0);
  const idFrontRef = useRef(null);
  const idBackRef = useRef(null);
  const [idFrontPreview, setIdFrontPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);
  // Template/crop mode removed per request; we rely on label-based parsing only
  // UI requirement: mimic car-docs.ro upload area (dashed dropzone and helper text)
  const [isDragOver, setIsDragOver] = useState(false);

  // Invoice PDF mapping state (pentru mașini de pe site)
  const [invoiceFields, setInvoiceFields] = useState([]); // [{name,type}]
  const [invoiceMapping, setInvoiceMapping] = useState({}); // semantic -> actual field name
  const [showInvoiceMapping, setShowInvoiceMapping] = useState(false);

  // Buyer state
  const [buyer, setBuyer] = useState({
    firstName: '',
    lastName: '',
    cnp: '',
    ciSeries: '',
    ciNumber: '',
    address: '',
    street: '',
    streetNumber: '',
    county: '',
    postalCode: '',
    villageOrSector: '',
    issuer: '',
    expireAt: '',
  });
  // Full name input state to preserve user-typed spaces (incl. trailing)
  const [fullNameInput, setFullNameInput] = useState('');
  const suppressFullNameSyncRef = useRef(false);

  // Seller (prefilled via backend if available)
  const [seller, setSeller] = useState({
    name: 'SC Exemplu SRL',
    cui: 'RO12345678',
    address: 'Str. Exemplu 1, București',
  });

  // Manual car extras editable near the car pick (registration and price overrides)
  const [carExtras, setCarExtras] = useState({ registration: '', price: '' });

  // Daily EUR→RON (BNR) rate
  const [eurRate, setEurRate] = useState(null); // number
  const [eurRateDate, setEurRateDate] = useState(null); // YYYY-MM-DD
  const [eurRateErr, setEurRateErr] = useState(null);
  // Buy-back UI state
  const [buybackEnabled, setBuybackEnabled] = useState(false);
  const [buybackList, setBuybackList] = useState([]);
  const [selectedBuybackId, setSelectedBuybackId] = useState('');
  const [buybackCar, setBuybackCar] = useState({
    marca: '', model: '', year: '', vin: '', km: '', capacitate: '', norma: '', registration: '',
  });
  // Talon upload state (for auto-fill via Google Vision)
  const talonRef = useRef(null);
  const [talonPreview, setTalonPreview] = useState(null);
  const [talonStatus, setTalonStatus] = useState('idle'); // idle | ocr
  // Mapping for Contract-completabil2.pdf (buy-back only)
  const [fillableFields2, setFillableFields2] = useState([]);
  const [fieldMapping2, setFieldMapping2] = useState({});

  useEffect(() => {
    fetch(apiUrl('/api/cars'))
      .then(r => r.ok ? r.json() : [])
      .then(setCars)
      .catch(() => setCars([]));
    fetch(apiUrl('/api/contracts/seller'))
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => data && setSeller(data))
      .catch(() => {});
    // Fetch invoice field names; load saved mapping from localStorage
    fetch(apiUrl('/api/contracts/templates/invoice/fields'))
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setInvoiceFields(data?.fields || []))
      .catch(() => setInvoiceFields([]));
    try {
      const saved = localStorage.getItem('invoiceFieldMapping');
      if (saved) setInvoiceMapping(JSON.parse(saved));
    } catch {}
    // Fetch BNR EUR rate
    fetch(apiUrl('/api/rates/eur'))
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { setEurRate(typeof d?.rate === 'number' ? d.rate : null); setEurRateDate(d?.date || null); })
      .catch(() => setEurRateErr('rate'));

    // Try to fetch buy-back list (requires auth). If no token or 401, ignore.
    try {
      const token = localStorage.getItem('token');
      if (token) {
        fetch(apiUrl('/api/buyback'), { headers: { Authorization: token } })
          .then(r => r.ok ? r.json() : Promise.reject())
          .then(list => setBuybackList(Array.isArray(list) ? list : []))
          .catch(() => setBuybackList([]));
      }
    } catch {}

    // Load fillable2 fields and saved mapping if available (buy-back only)
    fetch(apiUrl('/api/contracts/templates/fillable2/fields'))
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setFillableFields2(data?.fields || []))
      .catch(() => setFillableFields2([]));
    try {
      const saved2 = localStorage.getItem('fillableFieldMapping2');
      if (saved2) setFieldMapping2(JSON.parse(saved2));
    } catch {}
  }, []);

  // Auto-save invoice mapping whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('invoiceFieldMapping', JSON.stringify(invoiceMapping));
    } catch {}
  }, [invoiceMapping]);

  // Auto-save buy-back mapping whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('fillableFieldMapping2', JSON.stringify(fieldMapping2));
    } catch {}
  }, [fieldMapping2]);

  // Keep the full name input in sync when buyer names change programmatically (OCR, etc.)
  useEffect(() => {
    if (suppressFullNameSyncRef.current) return;
    const combined = `${buyer.lastName ? buyer.lastName + ' ' : ''}${buyer.firstName || ''}`;
    setFullNameInput(String(combined || '').toUpperCase());
  }, [buyer.firstName, buyer.lastName]);

  const selectedCar = useMemo(() => cars.find(c => c._id === selectedCarId), [cars, selectedCarId]);
  const selectedBuyback = useMemo(() => buybackList.find(b => b._id === selectedBuybackId), [buybackList, selectedBuybackId]);

  // When selecting a new car, prefill price (keep registration as typed)
  useEffect(() => {
    setCarExtras(prev => ({
      registration: prev.registration || '',
      price: (selectedCar && (selectedCar.price !== undefined && selectedCar.price !== null)) ? String(selectedCar.price) : (prev.price || ''),
    }));
  }, [selectedCarId]);

  // When selecting a buy-back entry, prefill buybackCar fields
  useEffect(() => {
    if (selectedBuyback) {
      setBuybackCar(prev => ({
        ...prev,
        marca: selectedBuyback.brand || selectedBuyback.marca || '',
        model: selectedBuyback.model || '',
        year: selectedBuyback.year || '',
        vin: selectedBuyback.vin || selectedBuyback.VIN || selectedBuyback.serie || '',
        km: selectedBuyback.km || '',
        capacitate: selectedBuyback.capacity || selectedBuyback.capacitate || '',
        norma: selectedBuyback.norma || selectedBuyback.normaEuro || selectedBuyback.euro || '',
        registration: selectedBuyback.inmatriculare || selectedBuyback.nrInmatriculare || '',
      }));
      // If a talon image exists for this buy-back entry, auto-load it as if the user uploaded it
      try {
        const talonPath = selectedBuyback.talon;
        if (talonPath) {
          const url = fileUrl(`/uploads/${talonPath}`);
          fetch(url)
            .then(r => r.ok ? r.blob() : Promise.reject(new Error('Nu pot descărca talonul')))
            .then(blob => {
              const file = new File([blob], 'talon.jpg', { type: blob.type || 'image/jpeg' });
              // Set input files so the UI thinks it's uploaded
              try {
                const dt = new DataTransfer();
                dt.items.add(file);
                if (talonRef.current) talonRef.current.files = dt.files;
              } catch {}
              // Show preview and run OCR extraction
              const previewUrl = URL.createObjectURL(blob);
              setTalonPreview(previewUrl);
              extractFromTalon(file);
            })
            .catch(() => {});
        }
      } catch {}
    }
  }, [selectedBuybackId]);

  const fieldLabel = { fontWeight: 700, color: tokens.text, marginBottom: 6, fontSize: 14 };
  const inputBase = {
    background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8,
    padding: '10px 12px', fontSize: 15, outline: 'none', width: '100%'
  };
  const selectStyle = { ...inputBase, appearance: 'none' };
  // Compact field width used across buyer (buletin) inputs and price box
  const compactWidth = isMobile ? '100%' : 220;
  // Shorter widths specifically for optional car extras (registration, price)
  const shortFieldWidth = isMobile ? '100%' : 160;
  const priceFieldWidth = isMobile ? '100%' : 160;

  // Price helpers: parse typed EUR, convert to RON using BNR rate
  const parseNumberFlexible = (s = '') => {
    if (typeof s === 'number') return s;
    const str = String(s).trim();
    if (!str) return NaN;
    // Remove spaces, handle both 15.990 or 15,990.50 styles
    const cleaned = str.replace(/\s+/g, '').replace(/[^0-9.,-]/g, '');
    if (!cleaned) return NaN;
    // If both separators present, assume last one is decimal
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let normalized = cleaned;
    if (lastComma !== -1 && lastDot !== -1) {
      if (lastComma > lastDot) {
        normalized = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = cleaned.replace(/,/g, '');
      }
    } else if (lastComma !== -1) {
      // only comma, treat as decimal
      const parts = cleaned.split(',');
      if (parts.length === 2 && parts[1].length === 3 && parts[0].length >= 1) {
        // Likely thousands grouping (e.g., 79,950) -> remove comma
        normalized = cleaned.replace(/,/g, '');
      } else {
        normalized = cleaned.replace(/\./g, '').replace(',', '.');
      }
    } else {
      // only dots or digits
      const parts = cleaned.split('.');
      if (parts.length > 2) normalized = cleaned.replace(/\./g, '');
      else if (parts.length === 2 && parts[1].length === 3 && parts[0].length >= 1) {
        // Likely thousands grouping (e.g., 15.990) -> remove dot
        normalized = cleaned.replace(/\./g, '');
      }
    }
    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
  };
  const formatEUR = (n) => new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
  const formatRON = (n) => new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
  const getEuroInputValue = () => {
    const val = (carExtras.price !== undefined && carExtras.price !== null && String(carExtras.price).trim() !== '')
      ? carExtras.price
      : (selectedCar?.price !== undefined && selectedCar?.price !== null ? selectedCar.price : '');
    return parseNumberFlexible(val);
  };
  const euroToRon = (eur) => {
    if (!eurRate || !Number.isFinite(eur)) return null;
    const ron = eur * eurRate;
    return Math.round(ron);
  };

  // Buy-back price inputs (EUR and manual conversion rate)
  const [buybackPriceEur, setBuybackPriceEur] = useState('');
  const [buybackRate, setBuybackRate] = useState(4.8);
  const buybackRon = useMemo(() => {
    const eur = parseNumberFlexible(buybackPriceEur);
    const rate = parseNumberFlexible(buybackRate);
    if (!Number.isFinite(eur) || !Number.isFinite(rate)) return null;
    return Math.round(eur * rate);
  }, [buybackPriceEur, buybackRate]);

  // Helper to parse street and number from a free-form address (e.g., "STR. SALCIILOR NR. 41")
  const parseStreetAndNumber = (addr='') => {
    const s = (addr || '').toString();
    let street = '';
    let number = '';
    // Find a street-like token up to a comma or before NR/NUMAR
    const streetMatch = s.match(/\b(STR\.?|STRADA|ALEEA|CALEA|BD\.?|BULEVARDUL|B-DUL|B-D)\s+[A-ZĂÂÎȘȚ0-9\-\s]+?(?=(?:\s*,|\s+NR\.?|\s+NUMAR|\s+NRUL|$))/i);
    if (streetMatch) {
      street = streetMatch[0].trim().replace(/\s+$/,'');
    }
    const nrMatch = s.match(/\b(?:NR\.?|NUMAR|NRUL)\s*([0-9A-ZĂÂÎȘȚ\-/]+)/i);
    if (nrMatch) {
      number = (nrMatch[1] || '').trim();
    }
    return { street, number };
  };

  // Parse administrative units (county, commune, village/sector) from a full address
  const parseAdminFromAddress = (addr='') => {
    const s = (addr || '').toString();
    const cleanup = (v='') => v.replace(/^[,\s]+|[,\s]+$/g,'').replace(/\s+/g,' ').trim();
    const out = { county: '', commune: '', villageOrSector: '' };
    const mCounty = s.match(/(?:JUDEȚUL|JUDETUL|JUD\.?)[\s:]*([^,]+)/i);
    if (mCounty) out.county = cleanup(mCounty[1]);
    const mCommune = s.match(/(?:COMUNA|COM\.?)[\s:]*([^,]+)/i);
    if (mCommune) out.commune = cleanup(mCommune[1]);
    const mSector = s.match(/(?:SECTOR)[\s:]*([^,]+)/i);
    if (mSector) out.villageOrSector = `Sector ${cleanup(mSector[1]).replace(/^SECTOR\s*/i,'')}`;
    const mSat = s.match(/(?:SAT(?:UL)?)\s*([^,]+)/i);
    if (mSat) out.villageOrSector = cleanup(mSat[1]);
    return out;
  };

  const onChangeBuyer = (e) => {
    const { name, value } = e.target;
    if (name === 'address') {
      const { street, number } = parseStreetAndNumber(value);
      const adm = parseAdminFromAddress(value);
      setBuyer(prev => ({
        ...prev,
        address: value,
        street,
        streetNumber: number,
        county: adm.county || prev.county,
        villageOrSector: adm.villageOrSector || prev.villageOrSector,
      }));
    } else {
      setBuyer(prev => ({ ...prev, [name]: value }));
    }
  };

  // Preprocess image (grayscale + contrast) similar to Cititor Buletin 1
  const preprocessImage = async (file) => {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = URL.createObjectURL(file);
    });
    const canvas = document.createElement('canvas');
    const scale = Math.min(2, 1400 / Math.max(img.width, img.height));
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = imgData.data;
    for (let p=0; p<d.length; p+=4){
      const r=d[p], g=d[p+1], b=d[p+2];
      let y = 0.2126*r + 0.7152*g + 0.0722*b;
      y = (y - 128) * 1.15 + 128;
      y = Math.max(0, Math.min(255, y));
      d[p]=d[p+1]=d[p+2]=y;
    }
    ctx.putImageData(imgData, 0, 0);
    return canvas;
  };

  // Preview handlers for uploaded images
  const handleFrontChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setIdFrontPreview(url);
      // Auto-run OCR immediately after selecting the image
      // Defer slightly to ensure input ref is populated
      setTimeout(() => { extractFromId(); }, 0);
    } else {
      setIdFrontPreview(null);
    }
  };
  const handleBackChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setIdBackPreview(url);
    } else {
      setIdBackPreview(null);
    }
  };

  // Talon change + preview
  const handleTalonChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setTalonPreview(null); return; }
    const url = URL.createObjectURL(file);
    setTalonPreview(url);
    await extractFromTalon(file);
  };

  // Use Google Vision on talon to extract car fields
  const extractFromTalon = async (file) => {
    try {
      const f = file || (talonRef.current && talonRef.current.files && talonRef.current.files[0]);
      if (!f) return alert('Încarcă poza talonului.');
      if (!/^image\//.test(f.type)) return alert('Selectează o imagine (JPEG/PNG/WebP).');
      if (f.size > 10 * 1024 * 1024) return alert('Dimensiune maximă 10MB.');
      setTalonStatus('ocr');
      const fd = new FormData();
      fd.append('talon', f);
      const resp = await fetch(apiUrl('/api/ocr/talon'), { method: 'POST', body: fd });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || 'Eroare OCR talon');
      }
      const j = await resp.json();
      const flds = j?.fields || {};
      setBuybackCar(prev => ({
        ...prev,
        marca: flds.marca || prev.marca,
        model: flds.model || prev.model,
        year: flds.year || prev.year,
        vin: flds.vin || prev.vin,
        capacitate: flds.capacitate || prev.capacitate,
        norma: flds.norma || prev.norma,
        registration: flds.registration || prev.registration,
      }));
    } catch (e) {
      alert(e?.message || 'Eroare la citirea talonului.');
    } finally {
      setTimeout(() => setTalonStatus('idle'), 250);
    }
  };

  // Cleanup object URLs to avoid memory leaks
  useEffect(() => {
    return () => {
      if (idFrontPreview) URL.revokeObjectURL(idFrontPreview);
      if (idBackPreview) URL.revokeObjectURL(idBackPreview);
    };
  }, [idFrontPreview, idBackPreview]);

  const extractFromId = async () => {
    const file = idFrontRef.current?.files?.[0];
    if (!file) return alert('Încarcă poza buletinului (față)');
    setStatus('ocr');
    setOcrProgress(5);
    try {
      // Force Google Vision only (no Tesseract fallback for testing)
      const fd = new FormData();
      fd.append('front', file);
      const back = idBackRef.current?.files?.[0];
      if (back) fd.append('back', back);
      const resp = await fetch(apiUrl('/api/ocr/google-text'), { method: 'POST', body: fd });
      if (!resp.ok) {
        const msg = await resp.text();
        throw new Error(msg || 'Google OCR endpoint error');
      }
      const j = await resp.json();
      if (!j?.rawText) throw new Error('Google OCR did not return text');
      const fields = extractFieldsFromText(j.rawText);
      setBuyer(prev => {
        const next = { ...prev };
        if (fields.prenume) next.firstName = fields.prenume;
        if (fields.nume) next.lastName = fields.nume;
        if (fields.cnp) next.cnp = fields.cnp;
        if (fields.serieCI) next.ciSeries = fields.serieCI;
        if (fields.numarCI) next.ciNumber = fields.numarCI;
        const parts = [];
        if (fields.strada) parts.push(`Str. ${fields.strada}`);
        if (fields.numar) parts.push(`Nr. ${fields.numar}`);
        if (fields.bloc) parts.push(`Bl. ${fields.bloc}`);
        if (fields.scara) parts.push(`Sc. ${fields.scara}`);
        if (fields.etaj) parts.push(`Et. ${fields.etaj}`);
        if (fields.apartament) parts.push(`Ap. ${fields.apartament}`);
        next.address = parts.join(', ');
        next.street = fields.strada || prev.street;
        next.streetNumber = fields.numar || prev.streetNumber;
        next.county = fields.judet || prev.county;
        next.postalCode = fields.codPostal || prev.postalCode;
        next.villageOrSector = fields.satSector || prev.villageOrSector;
        if (fields.eliberatDe) next.issuer = fields.eliberatDe;
        if (fields.valabilPanaLa) next.expireAt = fields.valabilPanaLa;
        return next;
      });
      setOcrProgress(100);
    } catch (err) {
      const msg = err?.message || 'Eroare la OCR. Încercați din nou.';
      alert(msg);
    } finally {
      setTimeout(() => { setStatus('idle'); setOcrProgress(0); }, 350);
    }
  };

  // Drag & drop handlers for the car-docs-like dropzone
  const onDropFiles = async (files) => {
    if (!files || !files.length) return;
    // accept only images and <= 10MB like car-docs hint
    const img = Array.from(files).find(f => /^image\//.test(f.type));
    if (!img) return alert('Selectează o imagine (JPEG/PNG/WebP).');
    if (img.size > 10 * 1024 * 1024) return alert('Dimensiune maximă 10MB.');
    // assign to hidden input to reuse existing flow
    try {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(img);
      if (idFrontRef.current) idFrontRef.current.files = dataTransfer.files;
    } catch {
      // Fallback for older browsers
    }
    // preview
    setIdFrontPreview(URL.createObjectURL(img));
    // auto-run OCR to mimic their UX after choosing image
    await extractFromId();
  };

  // Semantic values used for fillable PDF
  const computeSemanticValues = () => {
    // Derive brand with robust fallbacks since DB schema may not have `marca`/`brand`
    const brand = selectedCar?.marca
      || selectedCar?.brand
      || (selectedCar?.title ? String(selectedCar.title).trim().split(/\s+/)[0] : '')
      || '';
    // Extract numeric-only engine capacity (cm3) from various formats
    const extractEngineCc = (val) => {
      if (!val) return '';
      const s = String(val);
      // e.g., "2.0 L", "1,6 litri"
      const lit = s.match(/([0-9]+(?:[.,][0-9]+)?)\s*(l|litri|litru)\b/i);
      if (lit) {
        const n = parseFloat(lit[1].replace(',', '.'));
        if (!isNaN(n)) return String(Math.round(n * 1000));
      }
      // e.g., "1598 cm3", "2000cc"
      const cm = s.match(/(\d{3,5})\s*(?:cm3|cm|cc)\b/i);
      if (cm) return cm[1];
      // fallback: first group of 3-5 consecutive digits
      const group = s.match(/\d{3,5}/);
      if (group) return group[0];
      return '';
    };
    const engineCc = extractEngineCc(selectedCar?.capacitate);
    // Normalize a license plate-like input: keep as typed but trimmed
    const registration = (carExtras.registration || selectedCar?.nrInmatriculare || selectedCar?.inmatriculare || selectedCar?.registration || selectedCar?.plate || '')
      ? String(carExtras.registration || selectedCar?.nrInmatriculare || selectedCar?.inmatriculare || selectedCar?.registration || selectedCar?.plate || '').trim()
      : '';
    // Prefer manual price override if set; else use car.price (assumed EUR). Convert to RON using BNR rate.
    const eurVal = getEuroInputValue();
    const ronVal = euroToRon(eurVal);
    // Contract should receive only the numeric value (no currency label)
    const priceText = (ronVal && Number.isFinite(ronVal))
      ? `${formatRON(ronVal)}`
      : (String(selectedCar?.currency || '').toUpperCase() === 'RON'
        ? `${formatRON(parseNumberFlexible(selectedCar?.price || 0))}`
        : (String(carExtras.price || selectedCar?.price || '') || ''));
    const buildAddress = () => {
      if (buyer.address) return buyer.address;
      const parts = [];
      if (buyer.street) parts.push(`Str. ${buyer.street}`);
      if (buyer.streetNumber) parts.push(`Nr. ${buyer.streetNumber}`);
      if (buyer.bloc) parts.push(`Bl. ${buyer.bloc}`);
      if (buyer.scara) parts.push(`Sc. ${buyer.scara}`);
      if (buyer.etaj) parts.push(`Et. ${buyer.etaj}`);
      if (buyer.apartament) parts.push(`Ap. ${buyer.apartament}`);
      const tail = [buyer.villageOrSector, buyer.locality, buyer.county].filter(Boolean).join(', ');
      return [parts.join(', '), tail].filter(Boolean).join(', ');
    };
    return {
      seller_name: seller.name,
      seller_cui: seller.cui,
      seller_address: seller.address,
      buyer_name: `${buyer.lastName || ''} ${buyer.firstName || ''}`.trim(),
      buyer_firstname: buyer.firstName,
      buyer_lastname: buyer.lastName,
      buyer_cnp: buyer.cnp,
      buyer_ci_series: buyer.ciSeries,
      buyer_ci_number: buyer.ciNumber,
      buyer_address: buildAddress(),
  buyer_street: buyer.street,
  buyer_street_no: buyer.streetNumber,
  buyer_locality: buyer.locality,
  buyer_county: buyer.county,
  buyer_postal: buyer.postalCode,
  buyer_village_sector: buyer.villageOrSector,
  buyer_bloc: buyer.bloc || '',
  buyer_scara: buyer.scara || '',
  buyer_etaj: buyer.etaj || '',
  buyer_apartament: buyer.apartament || '',
      buyer_issuer: buyer.issuer,
      buyer_expire: buyer.expireAt,
      car_brand: brand,
      car_model: selectedCar?.model || '',
      car_year: selectedCar?.year || '',
      car_vin: selectedCar?.seriaSasiu || selectedCar?.vin || selectedCar?.VIN || '',
      car_km: selectedCar?.km || selectedCar?.kilometraj || '',
      car_price: priceText,
      car_registration: registration,
      car_engine_cc: engineCc,
    };
  };

  // Build invoice fields map with pre-mapped coordinates based on screenshot
  const buildInvoiceFieldsMap = () => {
    const values = computeSemanticValues();
    const eur = getEuroInputValue();
    const ron = euroToRon(eur);
    const priceWithoutVAT = ron && Number.isFinite(ron) ? ron / 1.19 : 0;
    const vat = ron && Number.isFinite(ron) ? ron - priceWithoutVAT : 0;
    const todayStr = new Date().toISOString().slice(0, 10).split('-').reverse().join('.');
    
    const invoiceValues = {
      ...values,
      serie_sasiu: values.car_vin,
      pret_unitar_fara_tva: priceWithoutVAT && Number.isFinite(priceWithoutVAT) ? String(Math.round(priceWithoutVAT)) : '',
      valoare_lei: ron && Number.isFinite(ron) ? String(Math.round(ron)) : '',
      valoare_tva_lei: vat && Number.isFinite(vat) ? String(Math.round(vat)) : '',
      cif_cnp: values.buyer_cnp,
      adresa: values.buyer_address,
      judet: values.buyer_county,
      data_emiterii: todayStr,
      total_plata: ron && Number.isFinite(ron) ? String(Math.round(ron)) : '',
      total_plata2: ron && Number.isFinite(ron) ? String(Math.round(ron)) : '',
      termen_plata: todayStr,
      total_fara_tva: priceWithoutVAT && Number.isFinite(priceWithoutVAT) ? String(Math.round(priceWithoutVAT)) : '',
      tva_la_incasare: vat && Number.isFinite(vat) ? String(Math.round(vat)) : '',
    };

    const out = {};
    Object.entries(invoiceMapping || {}).forEach(([semantic, actual]) => {
      if (actual && invoiceValues.hasOwnProperty(semantic)) {
        out[actual] = invoiceValues[semantic] ?? '';
      }
    });
    return out;
  };

  // Build fields map for Contract-completabil2.pdf using buyback car values
  const buildFieldsMapFromMapping2 = () => {
    const values = (() => {
      const brand = buybackCar.marca || buybackCar.brand || '';
      // Parse engine cc similar to main
      const extractEngineCc = (val) => {
        if (!val) return '';
        const s = String(val);
        const lit = s.match(/([0-9]+(?:[.,][0-9]+)?)\s*(l|litri|litru)\b/i);
        if (lit) { const n = parseFloat(lit[1].replace(',', '.')); if (!isNaN(n)) return String(Math.round(n * 1000)); }
        const cm = s.match(/(\d{3,5})\s*(?:cm3|cm|cc)\b/i);
        if (cm) return cm[1];
        const group = s.match(/\d{3,5}/);
        return group ? group[0] : '';
      };
      const buildAddress = () => {
        if (buyer.address) return buyer.address;
        const parts = [];
        if (buyer.street) parts.push(`Str. ${buyer.street}`);
        if (buyer.streetNumber) parts.push(`Nr. ${buyer.streetNumber}`);
        if (buyer.bloc) parts.push(`Bl. ${buyer.bloc}`);
        if (buyer.scara) parts.push(`Sc. ${buyer.scara}`);
        if (buyer.etaj) parts.push(`Et. ${buyer.etaj}`);
        if (buyer.apartament) parts.push(`Ap. ${buyer.apartament}`);
        const tail = [buyer.villageOrSector, buyer.locality, buyer.county].filter(Boolean).join(', ');
        return [parts.join(', '), tail].filter(Boolean).join(', ');
      };
      return {
        seller_name: seller.name,
        seller_cui: seller.cui,
        seller_address: seller.address,
        buyer_name: `${buyer.lastName || ''} ${buyer.firstName || ''}`.trim(),
        buyer_firstname: buyer.firstName,
        buyer_lastname: buyer.lastName,
        buyer_cnp: buyer.cnp,
        buyer_ci_series: buyer.ciSeries,
        buyer_ci_number: buyer.ciNumber,
        buyer_address: buildAddress(),
        buyer_street: buyer.street,
        buyer_street_no: buyer.streetNumber,
  buyer_locality: buyer.locality,
        buyer_county: buyer.county,
        buyer_postal: buyer.postalCode,
        buyer_village_sector: buyer.villageOrSector,
  buyer_bloc: buyer.bloc || '',
  buyer_scara: buyer.scara || '',
  buyer_etaj: buyer.etaj || '',
  buyer_apartament: buyer.apartament || '',
        buyer_issuer: buyer.issuer,
        buyer_expire: buyer.expireAt,
        car_brand: brand,
        car_model: buybackCar.model || '',
        car_year: buybackCar.year || '',
        car_vin: buybackCar.vin || '',
        car_km: buybackCar.km || '',
        car_engine_cc: extractEngineCc(buybackCar.capacitate),
        car_registration: buybackCar.registration || '',
        car_norma: buybackCar.norma || '',
        // Price in LEI for buy-back, computed from EUR and manual rate (default 4.8)
        car_price: (buybackRon && Number.isFinite(buybackRon)) ? String(buybackRon) : '',
      };
    })();
    const out = {};
    Object.entries(fieldMapping2 || {}).forEach(([semantic, actual]) => {
      if (actual && values.hasOwnProperty(semantic)) {
        out[actual] = values[semantic] ?? '';
      }
    });
    return out;
  };

  const resetMapping2 = () => {
    setFieldMapping2({});
    try { localStorage.removeItem('fillableFieldMapping2'); } catch {}
  };

  const resetInvoiceMapping = () => {
    setInvoiceMapping({});
    try { localStorage.removeItem('invoiceFieldMapping'); } catch {}
  };

  const generateContract = async () => {
    // Allow generating only buy-back if no car is selected
    setStatus('gen');
    try {
      if (!selectedCar) {
        if (!buybackEnabled) {
          setStatus('idle');
          return alert('Alege o mașină sau activează buy-back.');
        }
        // Generate ONLY the buy-back contract
        try {
          const fieldsMap2 = buildFieldsMapFromMapping2();
          const bbRon = (buybackRon && Number.isFinite(buybackRon)) ? buybackRon : undefined;
          const payload2 = { buyer, seller, buybackCar: {
            marca: buybackCar.marca, brand: buybackCar.marca,
            model: buybackCar.model, year: buybackCar.year,
            vin: buybackCar.vin, km: buybackCar.km,
            registration: buybackCar.registration,
            capacitate: buybackCar.capacitate,
            norma: buybackCar.norma,
            price: bbRon !== undefined ? bbRon : ''
          }, fieldsMap: fieldsMap2 };
          const res2 = await fetch(apiUrl('/api/contracts/generate-buyback?inline=1'), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload2)
          });
          if (!res2.ok) throw new Error(await res2.text());
          const blob2 = await res2.blob();
          const url2 = window.URL.createObjectURL(blob2);
          setLastGenerated(null);
          setLastGeneratedBuyback({ blobUrl: url2, ts: Date.now() });
        } catch (e) {
          console.error('Buy-back only preview error', e);
          alert('Contractul buy-back nu a putut fi generat. Verifică template-ul și maparea.');
        }
        return;
      }

      // Generate the main car INVOICE (not contract)
      const invoiceFieldsMap = buildInvoiceFieldsMap();
      const eur = getEuroInputValue();
      const ron = euroToRon(eur);
      
      // Normalize car data for invoice
      const carData = {
        marca: selectedCar.marca || selectedCar.brand || '',
        brand: selectedCar.brand || selectedCar.marca || '',
        model: selectedCar.model || '',
        year: selectedCar.year || '',
        vin: selectedCar.vin || selectedCar.VIN || '',
        km: selectedCar.km || selectedCar.kilometraj || '',
        price: ron && Number.isFinite(ron) ? ron : (selectedCar.price || ''),
      };

      const payload = { buyer, seller, car: carData, fieldsMap: invoiceFieldsMap };
      const res = await fetch(apiUrl('/api/contracts/generate-invoice?inline=1'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!res.ok) {
        let msg = 'Invoice endpoint not available';
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const j = await res.json();
            msg = j?.message || j?.error || JSON.stringify(j);
          } else { msg = await res.text(); }
        } catch {}
        throw new Error(msg || 'Invoice endpoint not available');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setLastGenerated({ blobUrl: url, ts: Date.now() });

      // Optionally also generate buy-back preview
      if (buybackEnabled) {
        try {
          const fieldsMap2 = buildFieldsMapFromMapping2();
          const bbRon = (buybackRon && Number.isFinite(buybackRon)) ? buybackRon : undefined;
          const payload2 = { buyer, seller, buybackCar: {
            marca: buybackCar.marca, brand: buybackCar.marca,
            model: buybackCar.model, year: buybackCar.year,
            vin: buybackCar.vin, km: buybackCar.km,
            registration: buybackCar.registration,
            capacitate: buybackCar.capacitate,
            norma: buybackCar.norma,
            price: bbRon !== undefined ? bbRon : ''
          }, fieldsMap: fieldsMap2 };
          const res2 = await fetch(apiUrl('/api/contracts/generate-buyback?inline=1'), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload2)
          });
          if (res2.ok) {
            const blob2 = await res2.blob();
            const url2 = window.URL.createObjectURL(blob2);
            setLastGeneratedBuyback({ blobUrl: url2, ts: Date.now() });
          } else {
            const errTxt = await res2.text();
            console.warn('Buy-back PDF preview failed:', errTxt);
          }
        } catch (e) { console.error('Buy-back auto preview error', e); }
      } else {
        if (lastGeneratedBuyback?.blobUrl) { try { URL.revokeObjectURL(lastGeneratedBuyback.blobUrl); } catch {} }
        setLastGeneratedBuyback(null);
      }
    } catch (e) {
      const msg = (e && e.message) ? e.message : 'Generarea contractului nu este disponibilă încă.';
      alert(msg);
    } finally {
      setStatus('idle');
    }
  };

  // Keep a reference to the last generated blob for quick download from UI
  const [lastGenerated, setLastGenerated] = useState(null);
  // Separate preview for buy-back contract
  const [lastGeneratedBuyback, setLastGeneratedBuyback] = useState(null);

  // Revoke blob URLs when replaced or on unmount
  useEffect(() => {
    return () => {
      try { if (lastGenerated?.blobUrl) URL.revokeObjectURL(lastGenerated.blobUrl); } catch {}
    };
  }, [lastGenerated]);

  // Revoke buyback blob URLs as well
  useEffect(() => {
    return () => {
      try { if (lastGeneratedBuyback?.blobUrl) URL.revokeObjectURL(lastGeneratedBuyback.blobUrl); } catch {}
    };
  }, [lastGeneratedBuyback]);

  // (butonul de previzualizare buy-back a fost eliminat; previzualizarea se generează automat la "Generează contract")

  const downloadContract = async () => {
    try {
      if (!selectedCar) {
        // If no car, but buy-back is enabled, download only the buy-back contract
        if (!buybackEnabled) return;
        const fieldsMap2 = buildFieldsMapFromMapping2();
        const bbRon = (buybackRon && Number.isFinite(buybackRon)) ? buybackRon : undefined;
        const payload2 = { buyer, seller, buybackCar: {
          marca: buybackCar.marca, brand: buybackCar.marca,
          model: buybackCar.model, year: buybackCar.year,
          vin: buybackCar.vin, km: buybackCar.km,
          registration: buybackCar.registration,
          capacitate: buybackCar.capacitate,
          norma: buybackCar.norma,
          price: bbRon !== undefined ? bbRon : '',
        }, fieldsMap: fieldsMap2 };
        const res2 = await fetch(apiUrl('/api/contracts/generate-buyback'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload2) });
        if (!res2.ok) throw new Error(await res2.text());
        const blob2 = await res2.blob();
        const url2 = window.URL.createObjectURL(blob2);
        const a2 = document.createElement('a');
        a2.href = url2;
        const name2 = `contract-buyback-${(buybackCar.marca||'auto')}-${(buybackCar.model||'')}-${(buybackCar.year||'')}`.replace(/[^a-z0-9\-]+/gi,'_');
        a2.download = `${name2}.pdf`;
        document.body.appendChild(a2);
        a2.click();
        a2.remove();
        window.URL.revokeObjectURL(url2);
        return;
      }

      const invoiceFieldsMap = buildInvoiceFieldsMap();
      const eur = getEuroInputValue();
      const ron = euroToRon(eur);
      
      const carData = {
        marca: selectedCar.marca || selectedCar.brand || '',
        brand: selectedCar.brand || selectedCar.marca || '',
        model: selectedCar.model || '',
        year: selectedCar.year || '',
        vin: selectedCar.vin || selectedCar.VIN || '',
        km: selectedCar.km || selectedCar.kilometraj || '',
        price: ron && Number.isFinite(ron) ? ron : (selectedCar.price || ''),
      };

      const payload = { buyer, seller, car: carData, fieldsMap: invoiceFieldsMap };
      const res = await fetch(apiUrl('/api/contracts/generate-invoice'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!res.ok) {
        try {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const j = await res.json();
            const msg = j?.message || j?.error || 'Download failed';
            throw new Error(msg);
          }
        } catch {}
        throw new Error('Download failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = `FACTURA-${selectedCar?.marca || selectedCar?.brand || 'auto'}-${selectedCar?.model || ''}`.replace(/[^a-z0-9\-]+/gi,'_');
      a.download = `${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      // If buy-back is enabled, also generate the buy-back contract using Contract-completabil2.pdf
      if (buybackEnabled) {
        try {
          const fieldsMap2 = buildFieldsMapFromMapping2();
          // Compute RON price for buy-back from EUR and chosen rate
          const bbRon = (buybackRon && Number.isFinite(buybackRon)) ? buybackRon : undefined;
          const payload2 = { buyer, seller, buybackCar: {
            marca: buybackCar.marca, brand: buybackCar.marca,
            model: buybackCar.model, year: buybackCar.year,
            vin: buybackCar.vin, km: buybackCar.km,
            registration: buybackCar.registration,
            capacitate: buybackCar.capacitate,
            norma: buybackCar.norma,
            price: bbRon !== undefined ? bbRon : '',
          }, fieldsMap: fieldsMap2 };
          const res2 = await fetch(apiUrl('/api/contracts/generate-buyback'), {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload2)
          });
          if (res2.ok) {
            const blob2 = await res2.blob();
            const url2 = window.URL.createObjectURL(blob2);
            const a2 = document.createElement('a');
            a2.href = url2;
            const name2 = `contract-buyback-${(buybackCar.marca||'auto')}-${(buybackCar.model||'')}-${(buybackCar.year||'')}`.replace(/[^a-z0-9\-]+/gi,'_');
            a2.download = `${name2}.pdf`;
            document.body.appendChild(a2);
            a2.click();
            a2.remove();
            window.URL.revokeObjectURL(url2);
          } else {
            const errTxt = await res2.text();
            console.warn('Buy-back PDF failed:', errTxt);
            alert('Contractul buy-back nu a putut fi generat. Verifică existența Contract-completabil2.pdf și maparea.');
          }
        } catch (e) {
          console.error(e);
          alert('Eroare la generarea contractului buy-back.');
        }
      }

      // After successful download, ask status action
      const action = window.prompt('Stare anunț: scrie "vanduta", "rezervata" sau "nimic" (fără schimbare).', (!selectedCar?.sold && selectedCar?.status !== 'sold') ? 'vanduta' : 'nimic');
      const norm = (action || '').toLowerCase().trim();
      if (norm === 'vanduta' || norm === 'vândută' || norm === 'vânduta' || norm === 'sold') {
        try {
          const token = localStorage.getItem('token');
          const resp = await fetch(apiUrl(`/api/cars/${selectedCar._id}`), {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              // Backend expects the raw token in Authorization header (no Bearer prefix)
              ...(token ? { Authorization: token } : {}),
            },
            body: JSON.stringify({ sold: true, status: 'sold' }),
          });
          if (!resp.ok) {
            const txt = await resp.text();
            throw new Error(txt || 'Nu s-a putut marca vândut.');
          }
          alert('Anunțul a fost marcat ca vândut.');
        } catch (err) {
          console.error(err);
          alert('Nu am putut marca anunțul ca vândut. Verifică autentificarea și încearcă din nou.');
        }
      } else if (norm === 'rezervata' || norm === 'rezervată' || norm === 'reserved' || norm === 'rez') {
        try {
          const token = localStorage.getItem('token');
          const resp = await fetch(apiUrl(`/api/cars/${selectedCar._id}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
            body: JSON.stringify({ status: 'reserved' }),
          });
          if (!resp.ok) throw new Error(await resp.text());
          alert('Anunțul a fost marcat ca rezervat.');
        } catch (err) {
          console.error(err);
          alert('Nu am putut marca anunțul ca rezervat.');
        }
      } else if (norm === 'nimic' || norm === 'clear' || norm === 'gol' || norm === 'anuleaza' || norm === 'anulează') {
        // no change
      }
    } catch (e) {
      const msg = (e && e.message) ? e.message : 'Download indisponibil momentan.';
      alert(msg);
    }
  };

  const downloadBuybackContract = async () => {
    try {
      if (!buybackEnabled) return;
      const fieldsMap2 = buildFieldsMapFromMapping2();
      const bbRon = (buybackRon && Number.isFinite(buybackRon)) ? buybackRon : undefined;
      const payload2 = { buyer, seller, buybackCar: {
        marca: buybackCar.marca, brand: buybackCar.marca,
        model: buybackCar.model, year: buybackCar.year,
        vin: buybackCar.vin, km: buybackCar.km,
        registration: buybackCar.registration,
        capacitate: buybackCar.capacitate,
        norma: buybackCar.norma,
        price: bbRon !== undefined ? bbRon : '',
      }, fieldsMap: fieldsMap2 };
      const res2 = await fetch(apiUrl('/api/contracts/generate-buyback'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload2) });
      if (!res2.ok) throw new Error(await res2.text());
      const blob2 = await res2.blob();
      const url2 = window.URL.createObjectURL(blob2);
      const a2 = document.createElement('a');
      a2.href = url2;
      const name2 = `contract-buyback-${(buybackCar.marca||'auto')}-${(buybackCar.model||'')}-${(buybackCar.year||'')}`.replace(/[^a-z0-9\-]+/gi,'_');
      a2.download = `${name2}.pdf`;
      document.body.appendChild(a2);
      a2.click();
      a2.remove();
      window.URL.revokeObjectURL(url2);
    } catch (e) {
      alert(e?.message || 'Download buy-back eșuat');
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gap: 18 }}>
        {/* Header */}
        <div style={{ background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 16, overflow: 'hidden' }}>
          <div style={{ background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`, color: tokens.inverse, padding: '14px 16px', fontWeight: 900, letterSpacing: .5, fontSize: 18 }}>
            {t.title}
          </div>
          <div style={{ padding: 16 }}>
            {/* Choose car + Buy-back parallel section */}
            <div style={{ marginBottom: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 30 }}>
              <div>
                <div style={fieldLabel}>{t.chooseCar}</div>
                <select value={selectedCarId} onChange={(e) => setSelectedCarId(e.target.value)} style={{ ...selectStyle, width: '60%' }}>
                <option value="">{t.pickCarPlaceholder}</option>
                {cars.map(c => (
                  <option key={c._id} value={c._id}>
                    {(c.marca || c.brand || (c.title?.split(' ')[0]) || '').toString()} {c.model || ''} · {c.year || ''} · {c.seriaSasiu || c.vin || c.VIN || ''}
                  </option>
                ))}
              </select>
              {selectedCar && (
                <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', color: tokens.textMuted }}>
                  {selectedCar.image && (
                    <img src={selectedCar.image.startsWith('http') ? selectedCar.image : fileUrl(`/uploads/${selectedCar.image}`)} alt="car" style={{ width: 96, height: 64, objectFit: 'cover', borderRadius: 8, border: `1px solid ${tokens.border}` }} />
                  )}
                  <div style={{ fontSize: 14 }}>
                    <div style={{ fontWeight: 700, color: tokens.text }}>{(selectedCar.marca || selectedCar.brand || (selectedCar.title?.split(' ')[0]) || '')} {selectedCar.model}</div>
                    <div style={{ opacity: .85, color: tokens.textMuted }}>{selectedCar.year} · VIN: {selectedCar.seriaSasiu || selectedCar.vin || selectedCar.VIN || '-'}</div>
                  </div>
                </div>
              )}

                {/* Extra fields: registration plate and price override */}
                <div style={{ marginTop: 15, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={fieldLabel}>Număr de înmatriculare (opțional)</div>
                  <input
                    type="text"
                    placeholder="B 12 ABC"
                    value={carExtras.registration}
                    onChange={(e) => setCarExtras(prev => ({ ...prev, registration: e.target.value }))}
                    style={{ ...inputBase, width: shortFieldWidth }}
                  />
                </div>
                <div>
                  <div style={fieldLabel}>Preț (opțional)</div>
                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 10, flexWrap: 'wrap' }}>
                    {/* Input with fixed euro prefix */}
                    <div style={{ position: 'relative', width: priceFieldWidth }}>
                      <span aria-hidden style={{ position: 'absolute', left: 10, top: '45%', transform: 'translateY(-50%)', color: tokens.textMuted, fontWeight: 700 }}>€</span>
                      <input
                        type="text"
                        placeholder={selectedCar?.price ? String(selectedCar.price) : 'ex: 24999'}
                        value={carExtras.price}
                        onChange={(e) => setCarExtras(prev => ({ ...prev, price: e.target.value }))}
                        style={{ ...inputBase, paddingLeft: 28 }}
                      />
                    </div>
                    {/* EUR->RON conversion preview to the right */}
                    <div style={{ color: tokens.textMuted, fontSize: 14, whiteSpace: 'nowrap', flexBasis: '100%', marginTop: 4 }}>
                      {(() => {
                        const eur = getEuroInputValue();
                        const ron = euroToRon(eur);
                        if (!Number.isFinite(eur) || !eurRate) return null;
                        return (
                          <span>
                            {formatEUR(eur)} € = <b>{ron !== null ? formatRON(ron) : '-'}</b> Lei{eurRateDate ? ` (BNR ${new Date(eurRateDate).toLocaleDateString('ro-RO')})` : ''}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Large talon preview placed under the car section when buy-back is active */}
              {buybackEnabled && talonPreview && (
                <div style={{ marginTop: 16, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 12, background: tokens.surface, padding: 8 }}>
                  <div style={{ width: '100%', height: isMobile ? 220 : 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={talonPreview} alt="Previzualizare talon" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 8 }} />
                  </div>
                </div>
              )}
              </div>

              {/* Buy-back parallel section */}
              <div>
                <div style={fieldLabel}>Ai făcut buy-back?</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <input id="bb-enable" type="checkbox" checked={buybackEnabled} onChange={(e)=>setBuybackEnabled(e.target.checked)} />
                  <label htmlFor="bb-enable" style={{ color: tokens.text }}>Activează secțiunea buy-back</label>
                </div>
                {buybackEnabled && (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {/* Controls only; the large preview is rendered under the left car section */}
                    <div>
                      <div style={fieldLabel}>Alege o mașină din buy-back (opțional)</div>
                      <select value={selectedBuybackId} onChange={(e)=>setSelectedBuybackId(e.target.value)} style={{ ...selectStyle, width: isMobile ? '100%' : '90%' }}>
                        <option value="">— Selectează —</option>
                        {buybackList.map(b => (
                          <option key={b._id} value={b._id}>{(b.brand || b.marca || 'Marca')} {b.model || ''} · {b.year || ''} · {b.phone}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div style={fieldLabel}>Încarcă poza talonului</div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input type="file" accept="image/*" ref={talonRef} onChange={handleTalonChange} style={{ display: 'none' }} />
                        <button type="button" onClick={()=>talonRef.current?.click()} className="secondary" style={{ background: 'transparent', color: tokens.text, border:`1px solid ${tokens.mutedBorder}`, padding:'10px 12px', borderRadius:10, cursor:'pointer' }}>Alege poză</button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 15 }}>
                      <div>
                        <div style={fieldLabel}>Marcă</div>
                        <input value={buybackCar.marca} onChange={(e)=>setBuybackCar(prev=>({...prev, marca:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: Dacia" />
                      </div>
                      <div>
                        <div style={fieldLabel}>Model</div>
                        <input value={buybackCar.model} onChange={(e)=>setBuybackCar(prev=>({...prev, model:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: Logan" />
                      </div>
                      <div>
                        <div style={fieldLabel}>An</div>
                        <input value={buybackCar.year} onChange={(e)=>setBuybackCar(prev=>({...prev, year:e.target.value.replace(/[^0-9]/g,'').slice(0,4)}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: 2012" />
                      </div>
                      <div>
                        <div style={fieldLabel}>Capacitate cilindrică</div>
                        <input value={buybackCar.capacitate} onChange={(e)=>setBuybackCar(prev=>({...prev, capacitate:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: 1598 cm3 / 1.6 L" />
                      </div>
                      <div>
                        <div style={fieldLabel}>Normă Euro</div>
                        <input value={buybackCar.norma} onChange={(e)=>setBuybackCar(prev=>({...prev, norma:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: Euro 5" />
                      </div>
                      <div>
                        <div style={fieldLabel}>Număr de înmatriculare</div>
                        <input value={buybackCar.registration} onChange={(e)=>setBuybackCar(prev=>({...prev, registration:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: B 12 ABC" />
                      </div>
                      <div>
                        <div style={fieldLabel}>Serie șasiu (VIN)</div>
                        <input value={buybackCar.vin} onChange={(e)=>setBuybackCar(prev=>({...prev, vin:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: UU1XYZ..." />
                      </div>
                      <div>
                        <div style={fieldLabel}>Kilometraj</div>
                        <input value={buybackCar.km} onChange={(e)=>setBuybackCar(prev=>({...prev, km:e.target.value}))} style={{ ...inputBase, width: isMobile ? '100%' : '85%' }} placeholder="ex: 182000" />
                      </div>
                    </div>
                    {/* Buy-back price in EUR with manual conversion to LEI */}
                    <div style={{ display: 'flex', gap: 40, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <div>
                        <div style={fieldLabel}>Preț buy-back (EUR)</div>
                        <div style={{ position: 'relative', width: priceFieldWidth }}>
                          <span style={{ position: 'absolute', left: 10, top: 10, color: '#9ca3af', pointerEvents: 'none' }}>€</span>
                          <input
                            style={{ ...inputBase, paddingLeft: 20 }}
                            placeholder="ex: 1500"
                            value={buybackPriceEur}
                            onChange={(e) => setBuybackPriceEur(e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <div style={fieldLabel}>Curs (lei/EUR)</div>
                        <input
                          style={{ ...inputBase, width: priceFieldWidth }}
                          placeholder="4.8"
                          value={buybackRate}
                          onChange={(e) => setBuybackRate(e.target.value)}
                        />
                      </div>
                      <div style={{ color: tokens.textMuted, fontSize: 14, whiteSpace: 'nowrap', flexBasis: '100%' }}>
                        {(() => {
                          const eur = parseNumberFlexible(buybackPriceEur);
                          const rate = parseNumberFlexible(buybackRate);
                          if (!Number.isFinite(eur) || !Number.isFinite(rate)) return null;
                          const ron = Math.round(eur * rate);
                          return (
                            <span>
                              {formatEUR(eur)} € = <b>{formatRON(ron)}</b> Lei (curs {rate})
                            </span>
                          );
                        })()}
                        {eurRateErr && <div style={{color:'#ef4444', fontSize:12, marginTop:4}}>Eroare la obținerea cursului EUR.</div>}
                      </div>
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>La descărcare, se va genera și un al doilea PDF (Contract-completabil2.pdf) cu aceste date.</div>
                  </div>
                )}
              </div>
            </div>

            {/* Upload + Fields (Cititor Buletin 1 style) */}
            <div style={{ marginTop: 8 }}>
              <div style={{ ...fieldLabel }}>Completați datele cumpărătorului</div>
              <div
                onDragOver={(e)=>{e.preventDefault();setIsDragOver(true);}}
                onDragLeave={()=>setIsDragOver(false)}
                onDrop={(e)=>{e.preventDefault();setIsDragOver(false); onDropFiles(e.dataTransfer.files);}}
                onClick={()=>idFrontRef.current?.click()}
                role="button"
                aria-label="Încărcați o singură fotografie a buletinului (fața)"
                style={{
                  border: `2px dashed ${isDragOver ? tokens.accent : tokens.mutedBorder}`,
                  background: isDragOver ? tokens.surface : tokens.panel,
                  borderRadius: 12,
                  padding: 18,
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'background .2s, border-color .2s'
                }}
              >
                <div style={{ fontSize: 20 }} aria-hidden>📄</div>
                <div style={{ color: tokens.text, fontWeight: 700 }}>Click pentru a încărca buletinul</div>
                <input type="file" accept="image/*" ref={idFrontRef} onChange={handleFrontChange} hidden />
              </div>
              {idFrontPreview && (
                <div style={{ marginTop: 10 }}>
                  <img src={idFrontPreview} alt="Previzualizare buletin" style={{ width: '100%', maxWidth: 360, height: 180, objectFit: 'cover', borderRadius: 10, border: `1px solid ${tokens.border}`, background: tokens.surface }} />
                </div>
              )}
              <p style={{ color: tokens.textMuted, fontSize: 12, marginTop: 8 }}>
                <b>Atenție:</b> Încarcă o SINGURĂ poză: doar fața buletinului. Nu e nevoie de verso. Nu ne asumăm răspunderea pentru erorile de procesare a datelor din imagine. Verificați datele introduse.
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
                <button type="button" onClick={()=>{ if (idFrontRef.current) { idFrontRef.current.value=''; } setIdFrontPreview(null); }} className="secondary" style={{ background: 'transparent', color: tokens.text, border:`1px solid ${tokens.mutedBorder}`, padding:'10px 14px', borderRadius:10, cursor:'pointer' }}>Schimbă poza</button>
                <button type="button" onClick={extractFromId} disabled={!idFrontPreview || status==='ocr'} className="secondary" style={{ background: 'transparent', color: tokens.text, border:`1px solid ${tokens.mutedBorder}`, padding:'10px 14px', borderRadius:10, cursor: idFrontPreview ? 'pointer' : 'not-allowed', opacity: idFrontPreview ? 1 : .6 }}>{status==='ocr' ? 'Se citește…' : 'Citește automat din poză'}</button>
                <div style={{ color: tokens.textMuted, fontSize:12 }}>{status==='ocr' ? `Recunoaștere text… ${ocrProgress}%` : ''}</div>
              </div>
            </div>

            {/* Fields - compact, consistent width like price box */}
            <div style={{ marginTop: 14, background: tokens.surface, border:`1px solid ${tokens.border}`, borderRadius: 14, padding: 18 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Nume și Prenume</div>
                  <input
                    value={fullNameInput}
                    onChange={(e)=>{
                      const v = e.target.value || '';
                      // Preserve typing (including trailing space) and keep display uppercase
                      suppressFullNameSyncRef.current = true;
                      setFullNameInput(v.toUpperCase());
                      // Normalize to extract names without enforcing a trailing space
                      const norm = v.replace(/\s+/g, ' ').trim();
                      const parts = norm ? norm.split(' ') : [];
                      const last = parts.shift() || '';
                      const first = parts.join(' ');
                      setBuyer(prev=>({ ...prev, lastName:last.toUpperCase(), firstName:first.toUpperCase() }));
                      // Allow external sync after this render tick
                      setTimeout(() => { suppressFullNameSyncRef.current = false; }, 0);
                    }}
                    placeholder="Numele și prenumele"
                    style={inputBase}
                  />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Țara</div>
                  <select value={'Romania'} onChange={()=>{}} style={selectStyle}>
                    <option>Romania</option>
                  </select>
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Județ</div>
                  <select name="county" value={buyer.county} onChange={onChangeBuyer} style={selectStyle}>
                    <option value="">Alege județul</option>
                    {['Alba','Arad','Argeș','Bacău','Bihor','Bistrița-Năsăud','Botoșani','Brașov','Brăila','București','Buzău','Caraș-Severin','Călărași','Cluj','Constanța','Covasna','Dâmbovița','Dolj','Galați','Giurgiu','Gorj','Harghita','Hunedoara','Ialomița','Iași','Ilfov','Maramureș','Mehedinți','Mureș','Neamț','Olt','Prahova','Satu Mare','Sălaj','Sibiu','Suceava','Tulcea','Timiș','Teleorman','Vâlcea','Vaslui','Vrancea'].map(j=>(<option key={j} value={j}>{j}</option>))}
                  </select>
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Municipiul / Orașul / Comuna</div>
                  <input name="locality" value={buyer.locality||''} onChange={(e)=>setBuyer(prev=>({...prev, locality:e.target.value}))} placeholder="Ex: Sector 1 / Brașov / Ciugud" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Cod poștal</div>
                  <input name="postalCode" value={buyer.postalCode} onChange={(e)=>onChangeBuyer({ target:{ name:'postalCode', value:(e.target.value||'').replace(/\D/g,'').slice(0,6) }})} placeholder="Ex: 010101" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Sat / Sector</div>
                  <input name="villageOrSector" value={buyer.villageOrSector} onChange={onChangeBuyer} placeholder="Ex: Sat Poiana / Sector 3" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Strada</div>
                  <input name="street" value={buyer.street} onChange={onChangeBuyer} placeholder="Ex: Str. Luncii" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Număr</div>
                  <input name="streetNumber" value={buyer.streetNumber} onChange={onChangeBuyer} placeholder="Ex: 10" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Bloc</div>
                  <input name="bloc" value={buyer.bloc||''} onChange={(e)=>setBuyer(prev=>({...prev, bloc:e.target.value}))} placeholder="Ex: B2" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Scară</div>
                  <input name="scara" value={buyer.scara||''} onChange={(e)=>setBuyer(prev=>({...prev, scara:e.target.value}))} placeholder="Ex: 1" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Etaj</div>
                  <input name="etaj" value={buyer.etaj||''} onChange={(e)=>setBuyer(prev=>({...prev, etaj:e.target.value}))} placeholder="Ex: 3" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Apartament</div>
                  <input name="apartament" value={buyer.apartament||''} onChange={(e)=>setBuyer(prev=>({...prev, apartament:e.target.value}))} placeholder="Ex: 12" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Seria act de identitate</div>
                  <input name="ciSeries" value={buyer.ciSeries} onChange={(e)=>onChangeBuyer({ target:{ name:'ciSeries', value:(e.target.value||'').replace(/[^a-zA-Z]/g,'').toUpperCase().slice(0,2) }})} placeholder="Ex: AB" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>Număr act de identitate</div>
                  <input name="ciNumber" value={buyer.ciNumber} onChange={(e)=>onChangeBuyer({ target:{ name:'ciNumber', value:(e.target.value||'').replace(/\D/g,'').slice(0,6) }})} placeholder="Ex: 123456" style={inputBase} />
                </div>
                <div style={{ width: compactWidth }}>
                  <div style={fieldLabel}>CNP</div>
                  <input name="cnp" value={buyer.cnp} onChange={(e)=>onChangeBuyer({ target:{ name:'cnp', value:(e.target.value||'').replace(/\D/g,'').slice(0,13) }})} placeholder="13 cifre" style={inputBase} />
                  {buyer.cnp && (
                    <div style={{ marginTop: 6, fontSize: 12, color: isValidCNP(buyer.cnp) ? '#22c55e' : '#f43f5e' }}>
                      {isValidCNP(buyer.cnp) ? 'CNP valid' : 'CNP invalid — verifică cifrele'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Extra OCR details removed as requested */}

            {/* Seller preview */}
            <div style={{ marginTop: 18, background: tokens.surface, border: `1px dashed ${tokens.mutedBorder}`, padding: 12, borderRadius: 12 }}>
              <div style={{ ...fieldLabel, marginBottom: 4 }}>{t.seller}</div>
              <div style={{ color: tokens.textMuted }}>
                <div><b>Firma:</b> {seller.name}</div>
                <div><b>CUI:</b> {seller.cui}</div>
                <div><b>Adresă:</b> {seller.address}</div>
              </div>
            </div>

            {/* Invoice PDF mapping helper (pentru mașini de pe site) */}
            <div style={{ marginTop: 18, background: tokens.surface, border: `1px solid ${tokens.border}`, padding: 12, borderRadius: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ ...fieldLabel, margin: 0 }}>Mapare câmpuri PDF pentru Factură (mașini din site)</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowInvoiceMapping(v => !v)} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '8px 12px', fontWeight: 700 }}>
                    {showInvoiceMapping ? t.mapClose : t.mapOpen}
                  </button>
                  <button onClick={() => window.open(apiUrl('/api/contracts/preview/invoice-indexed'), '_blank')} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '8px 12px', fontWeight: 700 }}>
                    {t.mapIndexedPreview}
                  </button>
                </div>
              </div>
              <div style={{ color: tokens.textMuted, fontSize: 13, marginTop: 6 }}>Alege pentru fiecare valoare din date câmpul real din factura PDF. Câmpurile sunt deja pre-mapate conform screenshot-ului trimis.</div>
              {showInvoiceMapping && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ color: tokens.textMuted, fontSize: 12, marginBottom: 12 }}>
                    Mapare coordonate pentru factura PDF conform screenshot-ului trimis:
                    <br/>1=Serie șasiu, 2=Preț unitar (fără TVA), 3=Valoarea Lei, 4=Valoarea TVA lei, 5=CIF/CNP, 6=Adresa, 7=Județ, 8=Data emiterii, 9=TOTAL PLATA (font alb + bold), 10=TOTAL PLATA, 11=Termen plată, 12=Total (fără TVA), 13=TVA la încasare
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                    {[
                      { key: 'serie_sasiu', label: '1 · Serie șasiu (VIN)' },
                      { key: 'pret_unitar_fara_tva', label: '2 · Preț unitar (fără TVA)' },
                      { key: 'valoare_lei', label: '3 · Valoarea Lei (cu TVA)' },
                      { key: 'valoare_tva_lei', label: '4 · Valoarea TVA lei' },
                      { key: 'cif_cnp', label: '5 · CIF/CNP' },
                      { key: 'adresa', label: '6 · Adresa' },
                      { key: 'judet', label: '7 · Județ' },
                      { key: 'data_emiterii', label: '8 · Data emiterii' },
                      { key: 'total_plata', label: '9 · TOTAL PLATA (font alb + bold Arial)' },
                      { key: 'total_plata2', label: '10 · TOTAL PLATA' },
                      { key: 'termen_plata', label: '11 · Termen plată' },
                      { key: 'total_fara_tva', label: '12 · Total (fără TVA)' },
                      { key: 'tva_la_incasare', label: '13 · TVA la încasare' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <div style={{ color: tokens.text, marginBottom: 6, fontSize: 13 }}>{label}</div>
                        <select
                          value={invoiceMapping[key] || ''}
                          onChange={(e) => setInvoiceMapping(prev => ({ ...prev, [key]: e.target.value }))}
                          style={{ ...selectStyle }}
                        >
                          <option value="">{t.mapUnassigned}</option>
                          {invoiceFields.map(f => (
                            <option key={f.name} value={f.name}>{f.name}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ color: tokens.mutedText, fontSize: 13, fontStyle: 'italic' }}>{t.mapAutoSaved}</div>
                    <button onClick={resetInvoiceMapping} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '8px 12px', fontWeight: 800 }}>{t.mapReset}</button>
                  </div>

                  {/* Buy-back mapping for Contract-completabil2.pdf */}
                  <div style={{ marginTop: 18, borderTop: `1px dashed ${tokens.mutedBorder}`, paddingTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ ...fieldLabel, marginBottom: 8 }}>Mapare câmpuri PDF pentru Buy-back (Contract-completabil2.pdf)</div>
                      <button onClick={() => window.open(apiUrl('/api/contracts/preview/fillable2-indexed'), '_blank')} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '6px 10px', fontWeight: 700 }}>
                        Deschide previzualizare indexată (Buy-back)
                      </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                      {[
                        { key: 'buyer_lastname', label: 'Cumpărător · Nume' },
                        { key: 'buyer_firstname', label: 'Cumpărător · Prenume' },
                        { key: 'buyer_name', label: 'Cumpărător · Nume complet' },
                        { key: 'buyer_cnp', label: 'Cumpărător · CNP' },
                        { key: 'buyer_ci_series', label: 'Cumpărător · Serie CI' },
                        { key: 'buyer_ci_number', label: 'Cumpărător · Număr CI' },
                        { key: 'buyer_address', label: 'Cumpărător · Adresă' },
                        { key: 'buyer_street', label: 'Cumpărător · Stradă' },
                        { key: 'buyer_street_no', label: 'Cumpărător · Nr.' },
                        { key: 'buyer_bloc', label: 'Cumpărător · Bloc' },
                        { key: 'buyer_scara', label: 'Cumpărător · Scară' },
                        { key: 'buyer_etaj', label: 'Cumpărător · Etaj' },
                        { key: 'buyer_apartament', label: 'Cumpărător · Apartament' },
                        { key: 'buyer_locality', label: 'Cumpărător · Comuna / Localitate' },
                        { key: 'buyer_county', label: 'Cumpărător · Județ' },
                        { key: 'buyer_postal', label: 'Cumpărător · Cod poștal' },
                        { key: 'buyer_village_sector', label: 'Cumpărător · Sat/Sector' },
                        { key: 'buyer_issuer', label: 'Cumpărător · Emitent CI' },
                        { key: 'buyer_expire', label: 'Cumpărător · Data exp. CI' },
                        { key: 'car_brand', label: 'Buy-back · Marcă' },
                        { key: 'car_model', label: 'Buy-back · Model' },
                        { key: 'car_year', label: 'Buy-back · An' },
                        { key: 'car_vin', label: 'Buy-back · VIN' },
                        { key: 'car_km', label: 'Buy-back · Kilometraj' },
                        { key: 'car_engine_cc', label: 'Buy-back · Capacitate cilindrică' },
                        { key: 'car_registration', label: 'Buy-back · Înmatriculare' },
                        { key: 'car_norma', label: 'Buy-back · Normă Euro' },
                        { key: 'car_price', label: 'Buy-back · Preț (lei)' },
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <div style={{ color: tokens.text, marginBottom: 6, fontSize: 13 }}>{label}</div>
                          <select
                            value={fieldMapping2[key] || ''}
                            onChange={(e) => setFieldMapping2(prev => ({ ...prev, [key]: e.target.value }))}
                            style={{ ...selectStyle }}
                          >
                            <option value="">{t.mapUnassigned}</option>
                            {fillableFields2.map(f => (
                              <option key={f.name} value={f.name}>{f.name}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ color: tokens.mutedText, fontSize: 13, fontStyle: 'italic' }}>{t.mapAutoSaved}</div>
                      <button onClick={resetMapping2} style={{ background: tokens.panel, color: tokens.text, border: `1px solid ${tokens.mutedBorder}`, borderRadius: 8, padding: '8px 12px', fontWeight: 800 }}>{t.mapReset}</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
              <div style={{ display: 'flex', gap: 16, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={generateContract} style={{ background: themeKey==='darkNeon' ? 'linear-gradient(90deg, #00e676, #00b894)' : `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`, color: tokens.inverse, border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 900, boxShadow: `0 6px 18px ${tokens.indicatorGlow}` }}>
                {status === 'gen' ? t.statusGenerating : t.generate}
              </button>
              {lastGenerated?.blobUrl && (
                <button onClick={downloadContract} style={{ background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`, color: tokens.inverse, border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 900 }}>
                  Descarcă PDF
                </button>
              )}
              {lastGeneratedBuyback?.blobUrl && (
                <button onClick={downloadBuybackContract} style={{ background: `linear-gradient(135deg, ${tokens.accent}, ${tokens.accent2})`, color: tokens.inverse, border: 'none', borderRadius: 12, padding: '12px 18px', fontWeight: 900 }}>
                  Descarcă PDF Buy-back
                </button>
              )}
            </div>

            {lastGenerated?.blobUrl && (
              <div style={{ marginTop: 14 }}>
                <div style={{ color: tokens.textMuted, marginBottom: 6, fontSize: 12 }}>Previzualizare contract (editabil):</div>
                <iframe
                  title="contract-preview"
                  src={lastGenerated.blobUrl}
                  style={{ width: '100%', height: 700, border: `1px solid ${tokens.border}`, borderRadius: 12, background: tokens.bg }}
                />
              </div>
            )}

            {lastGeneratedBuyback?.blobUrl && (
              <div style={{ marginTop: 14 }}>
                <div style={{ color: tokens.textMuted, marginBottom: 6, fontSize: 12 }}>Previzualizare contract buy-back:</div>
                <iframe
                  title="contract-buyback-preview"
                  src={lastGeneratedBuyback.blobUrl}
                  style={{ width: '100%', height: 700, border: `1px solid ${tokens.border}`, borderRadius: 12, background: tokens.bg }}
                />
              </div>
            )}

            <div style={{ marginTop: 10, color: '#94a3b8', fontSize: 13 }}>{t.note}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
